import { Checkbox } from '@/ui/checkbox/checkbox';
import { Expander } from '@/ui/expander/expander';
import { Spinner } from '@/ui/spinner/spinner';
import { useMap } from '@vis.gl/react-google-maps';
import { useMemo, useState } from 'react';
import { defaultMapOptions } from '../../../../utils/map';
import { useCruisesAvailability } from '../../api/get-cruises-availability';
import { useCruiseSearch } from '../../api/search-cruises';
import { useFiltersContext } from '../../context/filters';
import { usePlaceInfoContext } from '../../context/place-info';
import { getFiltersAnalyticAttributes } from '../../utils/analytics';
import { cruiseTypes } from '../../utils/cruise';
import { defaultYears, monthOptions } from '../../utils/filters';
import classes from './filters-form.module.css';

const durations = [
  { min: 0, max: 8, label: 'Up to 8 days' },
  { min: 9, max: 11, label: '9-11 days' },
  { min: 12, max: 17, label: '12-17 days' },
  { min: 18, max: 23, label: '18-23 days' },
];

/**
 * @param {object} props
 * @param {() => void} props.filtersChange
 * @returns {React.JSX.Element | false}
 */
export function FiltersForm({ filtersChange }) {
  const map = useMap();
  const { filters, setFilters } = useFiltersContext();
  const { setPlaceInfo } = usePlaceInfoContext();
  const [formState, setFormState] = useState(filters);
  const { data: results, isFetching } = useCruiseSearch(formState);
  const { data: cruisesAvailability, isLoading } = useCruisesAvailability();
  const resultCount = results?.length ?? 0;

  const years = useMemo(
    () => (cruisesAvailability?.length ? cruisesAvailability : defaultYears),
    [cruisesAvailability]
  );

  const handleYearSelection = (/** @type {number} */ value) => {
    setFormState((prevState) => {
      const isSelected = prevState.years.includes(value);
      const years = isSelected ? prevState.years.filter((y) => y !== value) : [...prevState.years, value];
      return { ...prevState, years };
    });
  };

  const handleMonthSelection = (/** @type {number} */ value) => {
    setFormState((prevState) => {
      const isSelected = prevState.months.includes(value);
      const months = isSelected ? prevState.months.filter((m) => m !== value) : [...prevState.months, value];
      return { ...prevState, months };
    });
  };

  const handleCruiseTypeSelection = (/** @type {string} */ cruiseType) => {
    setFormState((prevState) => {
      const isSelected = prevState.cruises.includes(cruiseType);
      const cruises = isSelected
        ? prevState.cruises.filter((c) => c !== cruiseType)
        : [...prevState.cruises, cruiseType];
      return { ...prevState, cruises };
    });
  };

  const handleDurationSelection = (/**@type {{min: number; max: number}} */ duration) => {
    setFormState((prevState) => {
      const isSelected = prevState.durations.map((d) => d.min).includes(duration.min);
      const durations = isSelected
        ? prevState.durations.filter((d) => d.min !== duration.min)
        : [...prevState.durations, duration];
      return { ...prevState, durations };
    });
  };

  const showResults = () => {
    map?.moveCamera({
      center: new google.maps.LatLngBounds(defaultMapOptions.defaultBounds).getCenter(),
      zoom: defaultMapOptions.minZoom,
    });

    setFilters(formState);
    setPlaceInfo(null);
    filtersChange();
  };

  const clearAll = () => {
    setFormState({
      years: [],
      months: [],
      durations: [],
      cruises: [],
    });
  };

  const filtersAnalyticAttributes = useMemo(
    () => getFiltersAnalyticAttributes(formState, resultCount),
    [formState, resultCount]
  );

  return (
    <div className={classes.filtersForm}>
      {/* Voyage Type */}
      <div className={classes.filterSection}>
        <Expander isExpandable={false} title="Voyage Type">
          <div className={classes.cruiseTypes}>
            {cruiseTypes.map(({ label, value }) => {
              const isSelected = formState.cruises.includes(value);
              return (
                <Checkbox
                  key={value}
                  value={isSelected}
                  label={label}
                  valueChange={() => handleCruiseTypeSelection(value)}
                />
              );
            })}
          </div>
        </Expander>
      </div>
      {/* Years and Months */}
      <div className={classes.filterSection}>
        <Expander isExpandable={true} title="Departure Date">
          <div className={classes.date}>
            {isLoading ? (
              <div aria-label="Loading" className={classes.spinner}>
                <Spinner />
              </div>
            ) : (
              <>
                {years && years.length > 0 && (
                  <div>
                    <h2 className={classes.filterSubheading}>Year</h2>
                    <div className={classes.years}>
                      {years.map((year) => {
                        const isSelected = formState.years.includes(year);
                        return (
                          <Checkbox
                            key={year}
                            value={isSelected}
                            label={year.toString()}
                            valueChange={() => handleYearSelection(year)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                <div>
                  <h2 className={classes.filterSubheading}>Month</h2>
                  <div className={classes.months}>
                    {monthOptions.map((month) => {
                      const isSelected = formState.months.includes(month.value);
                      return (
                        <Checkbox
                          key={month.value}
                          value={isSelected}
                          label={month.label}
                          valueChange={() => handleMonthSelection(month.value)}
                        />
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </Expander>
      </div>
      {/* Duration */}
      <div className={classes.filterSection}>
        <Expander isExpandable={true} title="Duration">
          <div className={classes.durations}>
            {durations.map((duration, index) => {
              const isSelected = formState.durations.some((d) => d.min === duration.min);
              return (
                <Checkbox
                  key={index}
                  value={isSelected}
                  label={duration.label}
                  valueChange={() => handleDurationSelection(duration)}
                />
              );
            })}
          </div>
        </Expander>
      </div>
      <div className={classes.formActions}>
        <button
          type="button"
          className={classes.btnShowResults}
          onClick={showResults}
          disabled={isFetching || resultCount === 0 || !map}
          tl-event_name="fac_filter"
          {...filtersAnalyticAttributes}
        >
          {isFetching ? (
            <>Searching for itineraries...</>
          ) : (
            <>{resultCount > 0 ? 'Show Itineraries' : 'No Itineraries'}</>
          )}
        </button>
        <button
          type="button"
          className={classes.btnClearAll}
          onClick={clearAll}
          tl-event_name="fac_filter"
          tl-fac_category="clear_all"
        >
          Clear All
        </button>
      </div>
    </div>
  );
}
